/**
 * Render-time cleanup for WISP (CMS) post HTML.
 *
 * WISP posts can't be edited from this repo, so the prerender
 * (scripts/prerender-meta.mjs) and the post page (blogpost.jsx) both run the
 * HTML through cleanWispPostHtml() before showing it:
 *
 * - drop the "Powered by Synscribe" attribution footer,
 * - fix links whose host was swapped to chatgpt.com (keep the site path, or
 *   unwrap the link when it isn't one of our paths),
 * - point old top-level service URLs (/ai-agents, /frontend-development, …)
 *   at the live /services/* pages, and drop target/rel="nofollow" on links to
 *   our own pages,
 * - remove headings with no text,
 * - unwrap links to the retired GBP tool page (drop them if the text names it),
 * - apply POST_EDITS: per-post removal or sourcing of unsourced statistics and
 *   claims (see the PR that introduced this file for the full list).
 *
 * Local Markdown posts don't need this but pass through harmlessly.
 */

import { prependAnswerFirst } from "./blogEnhance.js";

const SITE = "https://www.jamilglobal.com";

/** Old top-level URLs used in WISP posts → live page (404s or extra hops today). */
const LEGACY_PATHS = {
  "/ai-agents": "/services/ai-agents",
  "/ai-integration": "/services/ai-integration",
  "/ai-automation": "/services/ai-automation",
  "/frontend-development": "/services/frontend",
  "/backend-development": "/services/backend",
  "/saas-development": "/services/saas-development",
  "/mobile-development": "/services/mobile-development",
  "/web-scraping-data-collection": "/services/scraping",
  "/how-we-do-it": "/what-we-do",
  "/contact": "/contact-us",
};

/** Paths we serve; a chatgpt.com/<one of these> link was meant for our site. */
const SITE_PATH_RE =
  /^\/(?:$|services\/|case-studies(?:\/|$)|custom-solutions$|contact-us$|our-work\/|dtaas$|blog(?:\/|$)|what-we-do$|products(?:\/|$)|ai-in-kuwait$)/;

const W3TECHS_WP = "https://w3techs.com/technologies/details/cm-wordpress";
const WP_PLUGINS = "https://wordpress.org/plugins/";

/**
 * Per-post claim edits: exact HTML `find` → `replace`. A drop is replace "".
 * Sources were checked on 2026-10-08. If WISP content changes so a `find` no
 * longer matches, the edit is skipped (the prerender logs it).
 */
export const POST_EDITS = {
  "what-is-wordpress-and-why-are-kuwait-businesses-still-building-with-it-in-2026": [
    {
      find: "It powers over 43% of all websites on the internet.",
      replace: `It powers about 40% of all websites on the internet (<a href="${W3TECHS_WP}">W3Techs</a>, as of October 8, 2026).`,
    },
    {
      find: "extended by over 60,000 plugins",
      replace: `extended by <a href="${WP_PLUGINS}">over 60,000 plugins</a>`,
    },
    {
      find: "Over 60,000 plugins and thousands of themes",
      replace: `<a href="${WP_PLUGINS}">Over 60,000 plugins</a> and thousands of themes`,
    },
  ],
  "wordpress-security-how-kuwait-and-gulf-brands-can-protect-their-websites-in-2026": [
    {
      find: "Over 90,000 WordPress websites are hacked every single day globally. ",
      replace: "",
    },
    {
      find: "popularity — powering over 43% of all websites globally — makes it the most targeted web platform for cyberattacks.",
      replace: `popularity — powering about 40% of all websites (<a href="${W3TECHS_WP}">W3Techs</a>, as of October 8, 2026) — makes it <a href="https://sucuri.net/reports/2023-hacked-website-report/">the most targeted web platform for cyberattacks</a>.`,
    },
  ],
  "how-ai-is-transforming-payment-processing-for-kuwait-and-gulf-brands-in-2026": [
    {
      find: "A <strong>Kuwait</strong> Visa card transaction might authorize at 95% through Tap Payments but only 88% through an alternative processor. ",
      replace: "",
    },
    {
      find: " For <strong>Kuwait</strong> businesses with multi-processor payment architectures, <strong>AI</strong> smart routing can increase overall authorization rates by several percentage points — which translates directly into significant additional revenue for <strong>Kuwait</strong> brands at scale.",
      replace: "",
    },
    {
      find: "<strong>AI</strong> payment capabilities are increasingly available to <strong>Kuwait</strong> businesses of all sizes through payment providers like Tap Payments and Stripe, which include <strong>AI</strong> fraud detection and analytics as standard features of their merchant packages. ",
      replace: "",
    },
  ],
  "is-replit-secure-enough-for-kuwait-and-gulf-business-applications-in-2026": [
    {
      find: "Replit is a US-based company with infrastructure primarily in US cloud regions.",
      replace: `Replit is a US-based company with infrastructure primarily in US cloud regions, according to its <a href="https://replit.com/privacy-policy">privacy policy</a>.`,
    },
    {
      find: "Replit complies with GDPR for European users. ",
      replace: "",
    },
  ],
  "why-every-kuwait-agency-should-be-using-claude-right-now": [
    {
      find: " The pattern is consistent: agencies that integrate Claude into their workflow produce more, pitch better, deliver faster, and retain clients longer. The ones that haven't made the move yet are already feeling the gap — they just haven't named it yet.",
      replace: "",
    },
    {
      find: " The efficiency gains — hours saved on content production, proposals, reporting, and communications — typically deliver ROI within the first month of deployment.",
      replace: "",
    },
  ],
  "10-wordpress-mistakes-kuwait-businesses-make-and-how-to-fix-them-with-ai": [
    {
      find: "<p><strong><em>Jamil Global fixes Kuwait WordPress mistakes faster and more effectively than any other agency in the Gulf — using AI to identify issues precisely, implement solutions efficiently, and prove results transparently.</em></strong></p>",
      replace: "",
    },
  ],
  "how-replit-is-changing-the-way-gulf-teams-collaborate-on-code-in-2026": [
    {
      find: " Most professional <strong>Kuwait</strong> and <strong>Gulf</strong> teams in 2026 use Replit for rapid development and collaboration, then integrate with Git for production code management.",
      replace: "",
    },
  ],
  "ai-backend-development-best-practices-mena": [
    {
      find: '<p>See <a target="_blank" rel="noopener" class="decorated-link" href="/our-work/profile-pro">Our Work – Profile Pro</a> for real-world optimized AI backend deployments.</p>',
      replace: "",
    },
  ],
};

/* Retired GBP tool page: one pattern covers its path and its name in link text. */
const RETIRED_TOOL_RE = /profile.?pro/i;
const OUR_WORK_HREF_RE = /^(?:https?:\/\/(?:www\.)?jamilglobal\.com)?\/our-work\//i;

function stripSynscribe(html) {
  return html
    .replace(
      /<p\b[^>]*>\s*(?:<small>\s*)?<a\b[^>]*synscribe\.com[^>]*>[\s\S]*?<\/a>\s*(?:<\/small>\s*)?<\/p>/gi,
      ""
    )
    .replace(/<a\b[^>]*synscribe\.com[^>]*>[\s\S]*?<\/a>/gi, "");
}

function splitPath(rest) {
  const m = /^([^?#]*)([\s\S]*)$/.exec(rest);
  const path = (m[1] || "/").replace(/\/+$/, "") || "/";
  return { path, tail: m[2] || "" };
}

function setAttr(attrs, name, value) {
  const re = new RegExp(`\\s${name}\\s*=\\s*("[^"]*"|'[^']*'|[^\\s>]+)`, "i");
  if (value === null) return attrs.replace(re, "");
  return re.test(attrs)
    ? attrs.replace(re, ` ${name}="${value}"`)
    : `${attrs} ${name}="${value}"`;
}

function fixLinks(html) {
  return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (whole, attrs, inner) => {
    const hrefMatch = /\shref\s*=\s*"([^"]*)"/i.exec(attrs);
    if (!hrefMatch) return whole;
    const href = hrefMatch[1];
    const hostMatch =
      /^https?:\/\/(?:www\.)?(chatgpt\.com|jamilglobal\.com)(\/[^\s"]*)?$/i.exec(href);
    if (!hostMatch) return whole;
    const host = hostMatch[1].toLowerCase();
    const { path, tail } = splitPath(hostMatch[2] || "/");
    const livePath = LEGACY_PATHS[path] || path;
    if (host === "chatgpt.com" && !SITE_PATH_RE.test(livePath)) {
      return inner; // not one of our pages: keep the text, drop the dead link
    }
    let next = setAttr(attrs, "href", `${SITE}${livePath === "/" ? "/" : livePath}${tail}`);
    next = setAttr(next, "target", null);
    next = setAttr(next, "rel", null);
    return `<a${next}>${inner}</a>`;
  });
}

function unwrapRetiredToolLinks(html) {
  return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (whole, attrs, inner) => {
    const href = /\shref\s*=\s*"([^"]*)"/i.exec(attrs)?.[1] ?? "";
    if (!OUR_WORK_HREF_RE.test(href) || !RETIRED_TOOL_RE.test(href)) return whole;
    return RETIRED_TOOL_RE.test(inner) ? "" : inner;
  });
}

function removeEmptyHeadings(html) {
  return html.replace(/<h([2-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi, (whole, _lvl, inner) =>
    inner.replace(/<[^>]+>|&nbsp;|&#160;|\s/gi, "") ? whole : ""
  );
}

/**
 * @param {string} html raw WISP post HTML
 * @param {string} slug post slug (selects POST_EDITS)
 * @param {{ onMissingEdit?: (slug: string, find: string) => void }} [opts]
 */
export function cleanWispPostHtml(html = "", slug = "", opts = {}) {
  let out = String(html || "");
  if (!out) return out;
  const edits = POST_EDITS[String(slug || "").trim().toLowerCase()] || [];
  for (const { find, replace } of edits) {
    if (out.includes(find)) out = out.split(find).join(replace);
    else if (opts.onMissingEdit) opts.onMissingEdit(slug, find);
  }
  out = stripSynscribe(out);
  out = unwrapRetiredToolLinks(out);
  out = fixLinks(out);
  out = removeEmptyHeadings(out);
  return prependAnswerFirst(out, slug);
}
